import React, {useState} from 'react';
import PropTypes from 'prop-types';
import {formatCurrency, formatDate} from "utils/i18n";
import ensureKeyed from '../../utils/ensureKeyed';
import {pluralize} from '../../utils/strings';
import {contracts} from '../../ducks/index';
import registeredComponent from "utils/registeredComponent";
import duckify, {singleDuck} from "utils/duckify";
import Placeholder from '../Placeholder';
import classNames from 'classnames';

import './styles/PaymentPlanFeeDetails.scss';

const PaymentPlanSchedule = ({
  paymentPlanPreview,
  selectedBillingPayerId,
  contract
}) => {
  const currentSplit = contracts.hooks.useBillingPayerSplit(contract, selectedBillingPayerId);

  return <React.Fragment>
    <div className='row'>
      <div className='col-md-12'>
        <table className={'table'}>
          <thead>
          <tr>
            <th>Payment</th>
            <th>Due Date</th>
            {!!contract.has_multiple_splits && !!currentSplit && <th>{currentSplit.billing_payer.payer_contact.name} Amount Due</th>}
            <th>Total Amount Due</th>
          </tr>
          </thead>
          <tbody>
          {
            paymentPlanPreview.schedule_preview
              .map(([schedule_item, amount, payer_splits], index) =>
                <tr className={'payment-plan-preview'}>
                  <td>{index + 1}</td>
                  <td className={'payment-plan-preview-due-date'}>
                    {
                      schedule_item.due_date_text || formatDate(schedule_item.due_date)
                    }
                  </td>
                  {!!contract.has_multiple_splits && !!currentSplit && <td className={'payment-plan-preview-amount payment-plan-preview-amount-split'}>{formatCurrency(payer_splits[currentSplit.billing_payer_id] || 0)}</td>}
                  <td className={'payment-plan-preview-amount payment-plan-preview-amount-total'}>{formatCurrency(amount)}</td>
                </tr>
              )
              .map(ensureKeyed())
          }
          </tbody>
        </table>
      </div>
    </div>
  </React.Fragment>;
};

const PaymentPlanFeeDetails = ({
  fee
}) => (
  <div className='payment-plan-fee'>
    <div className='payment-plan-fee-name'>
      {fee.name}
    </div>

    {
      !!fee.amount && (
        <div className={classNames('payment-plan-fee-amount', { 'payment-plan-fee-amount-negative': fee.amount < 0 })}>
          {formatCurrency(fee.amount)}
        </div>
      )
    }

    {
      !!fee.description && (
        <div className='payment-plan-fee-description'>
          {fee.description}
        </div>
      )
    }
  </div>
);

PaymentPlanFeeDetails.defaultProps = {
  fee: {}
};

PaymentPlanFeeDetails.propTypes = {
  fee: PropTypes.shape({
    amount: PropTypes.number,
    description: PropTypes.string,
    name: PropTypes.string
  })
};

const PaymentPlanDetails = ({
  duckMeta,
  contract,
  contractPaymentPlan,
  plan,
  checked,
  readonly,
  selectedBillingPayerId
}) => {
  const preview = contracts.hooks.usePaymentPlanPreview(contract, plan.id);
  const className = classNames('fee-list-item-details', {'fee-list-item-details-selected': checked});

  return <Placeholder.text
    ready={preview.data && !duckMeta.contract.writing && !duckMeta.contract.loading && preview.loaded}
    className={className}
    width={200}
    lines={plan.number_of_payments + 1}
    lineWidths={[200]}
    lastLineWidth={200}
    content={() => {

    if (preview.data.error) {
      return <div className={'alert alert-danger alert-danger'}>
        {preview.data.error}
      </div>;
    }

    const paymentPlanPreview = preview.data.payment_plan_preview;
    const fees = paymentPlanPreview.payment_plan_charges || [];

    return <div className={className}>
      {
        !!plan.contractual_text && (
          <div className={'fee-list-item'}>
            <div className={'sa-form-payment-plan-contractual-text'} dangerouslySetInnerHTML={{__html: plan.contractual_text}}/>
          </div>
        )
      }

      {!!fees.some(x=>x) &&
      <div className={'sa-form-payment-plan-plan-fees'}>
        <div className={'sa-form-payment-plan-included-fees'}>
          This plan includes the following {pluralize(fees.length, 'fee')}:
        </div>
        {
          fees
            .map(fee => <PaymentPlanFeeDetails fee={fee} checked={checked} />)
            .map(ensureKeyed())
        }
      </div>
      }

      {!!plan.confirmation_text &&
      <div className={'sa-form-payment-plan-confirmation-text'}>
        <div className={'sa-form-payment-plan-confirmation-check'}>
          <div className={'form-check form-fee-confirmation'}>
            <input
              type={'checkbox'}
              id={`${plan.canonical_name}_confirmation`}
              name={`${contract.form_name}[payment_plan_confirm][${plan.canonical_name}]`}
              value={'yes'}
              defaultChecked={readonly && checked}
              disabled={readonly}
              className={'form-check-input'}
            />
            <label
              className={'form-check-label confirmation-text required'}
              htmlFor={`${plan.canonical_name}_confirmation`}
            >
              {plan.confirmation_text}
            </label>
          </div>
        </div>
      </div>
      }

      {(contractPaymentPlan.school_year.billing_enabled || plan.use_payment_schedule) &&
        <PaymentPlanSchedule paymentPlanPreview={paymentPlanPreview}
                             contractPaymentPlan={contractPaymentPlan}
                             selectedBillingPayerId={selectedBillingPayerId}
                             contract={contract}
        />
      }
    </div>
  }}/>
};

const PaymentPlanSelector = ({
  duckMeta={},
  contract,
  contractPaymentPlan,
  readonly,
  paymentPlanCanonicalName,
  onPaymentPlanChange,
  selectedBillingPayerId
}) => {
  const plan = contractPaymentPlan.payment_plan;
  const checked = plan.canonical_name == paymentPlanCanonicalName;
  const scheduleMissing = (contractPaymentPlan.school_year.billing_enabled && !contractPaymentPlan.schedule);
  const disabled = readonly || scheduleMissing || duckMeta.contract.writing;

  return <div className={classNames(
    'fee-list-item',
    {'fee-list-item-selected': checked}
  )}>
    <div className={classNames('sa-form-payment-plan-item-explanation', {'selected': checked})}>
      <div className={'form-check'}>
        <input type={'radio'}
               className={'form-check-input fee-list-option'}
               name={`${contract.form_name}[payment_plan_canonical_name]`}
               id={plan.canonical_name}
               value={plan.canonical_name}
               checked={checked}
               onChange={onPaymentPlanChange}
               disabled={disabled}
        />
        <label className={'form-check-label'} htmlFor={plan.canonical_name}>
          &nbsp;
          {plan.explanation}
          &nbsp;
          {!!scheduleMissing &&
            <div className={'red'}>
              There are no valid schedules for this plan. Please contact the school if you wish to select this plan.
            </div>
          }

          {!!contractPaymentPlan.payment_plan_fee &&
            <span className={'fee-list-item-amount-inline'}>
              ({formatCurrency(contractPaymentPlan.payment_plan_fee)})
            </span>
          }
        </label>
      </div>
    </div>
    {!!checked &&
      <PaymentPlanDetails plan={plan}
                          contract={contract}
                          contractPaymentPlan={contractPaymentPlan}
                          checked={checked}
                          duckMeta={duckMeta}
                          readonly={readonly}
                          selectedBillingPayerId={selectedBillingPayerId}
      />
    }
  </div>
};

const PaymentPlanField = ({
  duckMeta,
  contract,
  required,
  readonly,
  allDucksReady,
  updateContract,
  selectedBillingPayerId
}) => {
  const [paymentPlanCanonicalName, setPaymentPlanCanonicalName] = useState();

  const selectPaymentPlan = (event) => {
    const payment_plan_canonical_name = event.target.value;
    setPaymentPlanCanonicalName(payment_plan_canonical_name);
    updateContract({...contract, payment_plan_canonical_name});
  };

  return <Placeholder.formField prompt={'Plan Options'} ready={allDucksReady} content={() =>
    <React.Fragment>
      <div className={'sa-form-field-group sa-form-payment-plan-group-header-row'}>
        <div className={'sa-form-field-group-prompt'}>
          <div className={classNames(
            'sa-form-payment-plan-group-header',
            {'sa-form-required-label': required}
          )}>
            Plan Options&nbsp;
          </div>
          <div className={'sa-form-payment-plan-group-header-details'}>
            Please select one option
          </div>

          {contract.errors && contract.errors.payment_plan &&
            <div className={'sa-form-field-group-body'}>
              <div className={'sa-form-payment-plan-error'}>
                <div className={'alert alert-danger'}>
                  Please select a payment plan below.
                </div>
              </div>
            </div>
          }

          {contract.errors && contract.errors.payment_plan_confirmation &&
            <div className={'sa-form-field-group-body'}>
              <div className={'sa-form-payment-plan-error'}>
                <div className={'alert alert-danger'}>
                  Please check the box to confirm payment plan details.
                </div>
              </div>
            </div>
          }
        </div>
      </div>

      <div className={'sa-form-field-group'}>
        <div className={'sa-form-field-group-prompt'} />
        <div className={'sa-form-field-group-body sa-form-payment-plans'}>
          <input type={'hidden'}
                 name={`${contract.form_name}[payment_plan_confirmation][sentinel]`}
                 value={'no'}/>
          {
            (contract.contract_payment_plans || [])
              .map(contractPaymentPlan => PaymentPlanSelector({
                contract,
                contractPaymentPlan,
                readonly,
                duckMeta,
                paymentPlanCanonicalName: paymentPlanCanonicalName || contract.payment_plan_canonical_name,
                onPaymentPlanChange: selectPaymentPlan,
                selectedBillingPayerId
              }))
              .map(ensureKeyed())
          }
        </div>
      </div>
    </React.Fragment>
  }/>
};

const mapDuckToProps = (props) => {
  const contract = singleDuck(contracts, props.contract_id);

  return {
    contract
  }
};

const mapStateToProps = (state) => ({
  selectedBillingPayerId: state.contracts.currentContract.selectedBillingPayerId
});

const mapDispatchToProps = (dispatch) => ({
  updateContract: (contract) => dispatch(contracts.actions.update(contract))
});

export default registeredComponent('PaymentPlanField')(
  duckify(mapDuckToProps, mapStateToProps, mapDispatchToProps)(PaymentPlanField)
);

export const unduckified = {
  PaymentPlanSchedule,
  PaymentPlanFeeDetails,
  PaymentPlanField,
  PaymentPlanSelector,
  PaymentPlanDetails
};
